import React, { MouseEvent } from 'react'
import { connect } from 'react-redux'
import { origins } from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import { publicAssetUrl } from '@chegg-wt/core/utils/url'
import {
  emitSuccessEvent,
  VIDEO_DEMAND_SUCCESS,
  userClick,
} from '@chegg-wt/core/state/instrumentation/actions'
import { AuthModalOrigin } from '@chegg-wt/core/components/Modals/AuthModal/types'
import { UNLOCK_ACCOUNT_GATE_MODAL } from '@chegg-wt/core/state/modals/constants'
import { trackWithoutThrow } from '@chegg-wt/core/utils/errors'
import { useRoutesContext } from '@chegg-wt/core/components/RoutesContext'
import { addRedirectQueryToRoute } from '@chegg-wt/core/utils/routes'
import { CancelIcon } from '@chegg-ui/theme'
import { Config } from '@chegg-wt/core/config'
import { navigated } from '@chegg-wt/core/state/app/actions'
import { formSubmitStopped } from '@chegg-wt/core/state/form/actions'
import { closeModal, unlockActivityCompleted } from '@chegg-wt/core/state/modals/actions'
import { modalDataSelector } from '@chegg-wt/core/state/modals/selectors'
import { InitialView } from '@chegg/one-auth'
import { startAuthFlow } from '@chegg-wt/core/state/user/actions'
import { ModalWrapper, DefaultBackdrop, PositioningWrapper } from '../styled'
import {
  BACKDROP,
  MODAL_CONTENT,
  CLOSE_BUTTON,
  TITLE_ICON,
  TITLE_TEXT,
  CREATE_ACCOUNT_BUTTON,
  UNLOCK_LINK,
  LOGIN_BANNER,
  LOGIN_LINK,
} from './testIds'
import { CLOSE, LOGIN, CREATE_ACCOUNT } from './constants'
import { UnlockAccountGateModalProps } from './types'
import * as Styled from './styled'

const CFC_MAX_WIDTH = '564px'

export const UnlockAccountGateModal: React.FC<UnlockAccountGateModalProps> = ({
  closeModal,

  modalData,
  unlockActivityCompleted,
  userClickClose,
  userClickLogin,
  userClickCreateAccount,
  userClickUnlock,
  navigated,
  formSubmitStopped,
  onVideoReady,
  startAuthFlow,
}) => {
  const {
    titleText,
    titleIconFilename,
    createAccountButtonText,
    canClose = true,
    fullMobileModal = true,
    maxWidth = CFC_MAX_WIDTH,
    unlock,
    origin,
    shouldRedirectAuth = false,
    redirectRoute,
  } = modalData

  const routes = useRoutesContext()
  const handleAuth = (defaultFlow: InitialView) => {
    startAuthFlow({
      defaultFlow: defaultFlow as InitialView,
      origin: modalData.origin as AuthModalOrigin,
    })
  }

  const handleCloseModal = () => {
    userClickClose(origin)

    if (origin === AuthModalOrigin.CITATION_FORM_PAGE) {
      formSubmitStopped()
    }

    closeModal(UNLOCK_ACCOUNT_GATE_MODAL)
  }

  const handleLoginClick = (event: MouseEvent<HTMLButtonElement>) => {
    userClickLogin(origin)

    if (shouldRedirectAuth) {
      event.preventDefault()
      navigated(addRedirectQueryToRoute(routes.toLogin(), redirectRoute?.href))
      return
    }

    handleAuth('signin')
  }

  const handleCreateAccountClick = (event: MouseEvent<Element>) => {
    userClickCreateAccount(origin)

    if (shouldRedirectAuth) {
      event.preventDefault()
      navigated(addRedirectQueryToRoute(routes.toSignup(), redirectRoute?.href))
      return
    }

    handleAuth('signup')
  }

  const handleUnlockClick = () => {
    const { completionAction, alwaysParticipate } = unlock
    userClickUnlock()
    closeModal(UNLOCK_ACCOUNT_GATE_MODAL)

    const unlockEligible = window.easybib_form_no_submit
    const unlockAvailable = window?.SBMGlobal?.run?.showUnlock !== undefined
    if (!(unlockEligible && unlockAvailable)) {
      unlockActivityCompleted({ completionAction })
      return
    }

    if (window.SBMGlobal.run.getUnlockStatus() === 'has-demand') {
      onVideoReady()
    }

    window.SBMGlobal.run
      .showUnlock({ skipStartView: true, alwaysParticipate })
      .catch(trackWithoutThrow)
      .then(() => unlockActivityCompleted({ completionAction }))
  }

  return (
    <ModalWrapper
      backdrop
      show
      renderBackdrop={() => <DefaultBackdrop data-test={BACKDROP} />}
    >
      <PositioningWrapper>
        <Styled.ModalContent
          fullMobileModal={fullMobileModal}
          maxWidth={maxWidth}
          data-test={MODAL_CONTENT}
        >
          {canClose && (
            <Styled.CloseButton
              screenReaderOnlyText="close modal"
              onClick={handleCloseModal}
              data-test={CLOSE_BUTTON}
            >
              <CancelIcon />
            </Styled.CloseButton>
          )}
          <Styled.ContentContainer>
            <Styled.TitleIcon
              src={publicAssetUrl(titleIconFilename)}
              data-test={TITLE_ICON}
            />
            <Styled.TitleText data-test={TITLE_TEXT}>{titleText}</Styled.TitleText>
            <Styled.CreateAccountButton
              onClick={handleCreateAccountClick}
              data-test={CREATE_ACCOUNT_BUTTON}
            >
              {createAccountButtonText}
            </Styled.CreateAccountButton>
            {unlock && (
              <Styled.UnlockLink onClick={handleUnlockClick} data-test={UNLOCK_LINK}>
                {unlock.message}
              </Styled.UnlockLink>
            )}
            <Styled.LoginBanner data-test={LOGIN_BANNER}>
              Already have an account?
              <Styled.LoginLink onClick={handleLoginClick} data-test={LOGIN_LINK}>
                Sign in
              </Styled.LoginLink>
            </Styled.LoginBanner>
          </Styled.ContentContainer>
        </Styled.ModalContent>
      </PositioningWrapper>
    </ModalWrapper>
  )
}

const mapStateToProps = (state) => ({
  modalData: modalDataSelector(state),
})

const mapDispatchToProps = {
  closeModal,
  unlockActivityCompleted,
  userClickClose: (origin: AuthModalOrigin) =>
    userClick({ origin: `${origin}-${CLOSE}` as const }),
  userClickLogin: (origin: AuthModalOrigin) =>
    userClick({
      origin: `${origin}-${LOGIN}` as const,
      options: { endpoint: Config.sbmRioEndpoint },
    }),
  userClickCreateAccount: (origin: AuthModalOrigin) =>
    userClick({
      origin: `${origin}-${CREATE_ACCOUNT}` as const,
      options: { endpoint: Config.sbmRioEndpoint },
    }),
  userClickUnlock: () =>
    userClick({
      origin: origins.accountGateModalClickUnlock,
      options: { endpoint: Config.sbmRioEndpoint },
    }),
  navigated,
  formSubmitStopped,
  onVideoReady: () => emitSuccessEvent({ viewName: VIDEO_DEMAND_SUCCESS }),
  startAuthFlow,
}

export default connect(mapStateToProps, mapDispatchToProps)(UnlockAccountGateModal)
